import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  BarChart3,
  CalendarCheck,
  CreditCard,
  FileInput,
  GitMerge,
  Inbox,
  LogIn,
  Mail,
  MousePointerClick,
  Paperclip,
  Pin,
  PinOff,
  ShieldAlert,
  ShoppingCart,
  StickyNote,
  Tag,
  Trash2,
  UserPlus,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import type { Note, TimelineItem } from "@/lib/types";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";
import { DisputeBadge } from "@/features/billing/PaymentBits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { TIMELINE_AREAS, useNoteChange, useNotes, useTimeline } from "./hooks";
import { LoadError } from "@/components/LoadError";

const icons: Record<string, LucideIcon> = {
  note: StickyNote,
  tag: Tag,
  email: Mail,
  form: FileInput,
  order: ShoppingCart,
  payment: CreditCard,
  payment_method: CreditCard,
  refund: CreditCard,
  invoice: CreditCard,
  subscription: CreditCard,
  login: LogIn,
  magic_link: LogIn,
  automation: Workflow,
  event: CalendarCheck,
  ga4: MousePointerClick,
  contact: UserPlus,
  merge: GitMerge,
  file: Paperclip,
};

const providerLabel: Record<string, string> = { ses: "Broadcast", postmark: "Postmark", keap: "Keap (imported)", gmail: "Gmail", manual: "Logged by hand" };

/** Notes, forms, emails, tag changes, automation steps, orders, payments, logins, GA4 visits. */
export function Timeline({ id }: { id: number }) {
  const [area, setArea] = useState("all");
  const areas = TIMELINE_AREAS.find((a) => a.key === area)?.areas ?? [];
  const t = useTimeline(id, areas);
  const notes = useNotes(id);
  const noteById = new Map((notes.data?.notes ?? []).map((n) => [String(n.id), n]));
  const items = t.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <div className="space-y-2">
      <div role="radiogroup" aria-label="Show on the timeline" className="flex flex-wrap gap-1">
        {TIMELINE_AREAS.map((a) => (
          <button
            key={a.key}
            role="radio"
            aria-checked={area === a.key}
            onClick={() => setArea(a.key)}
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-xs",
              area === a.key ? "border-[var(--color-primary)] bg-[var(--color-accent)] text-[var(--color-accent-foreground)]" : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]",
            )}
          >
            {a.label}
          </button>
        ))}
      </div>
      <Card>
        {t.isLoading && <Skeleton className="m-3 h-40" />}
        {t.error && <LoadError what="the timeline" error={t.error} onRetry={() => (t.isFetchNextPageError ? t.fetchNextPage() : t.refetch())} />}
        {!t.isLoading && !t.error && items.length === 0 && <Empty title="Nothing here yet." />}
        <ol className="divide-y">
          {items.map((it, i) => (
            <TimelineRow key={`${it.occurred_at}-${it.kind}-${it.ref_id ?? i}`} it={it} note={it.ref_type === "note" && it.ref_id ? noteById.get(it.ref_id) : undefined} contactId={id} />
          ))}
        </ol>
        {t.hasNextPage && (
          <div className="border-t p-2">
            <Button variant="ghost" size="sm" onClick={() => t.fetchNextPage()} disabled={t.isFetchingNextPage}>
              Show older
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}

export function TimelineRow({ it, note, contactId }: { it: TimelineItem; note?: Note; contactId: number }) {
  const area = it.kind.split(".")[0];
  // A chargeback (crm-billing's payment.disputed, one entry per status change) stands out in red with its status.
  const dispute = it.kind === "payment.disputed" ? ((it.data ?? {}) as { status?: string; reason?: string }) : null;
  const Icon = dispute ? ShieldAlert : it.kind === "email.received" ? Inbox : (icons[area] ?? StickyNote);
  const email =
    it.source === "email" ? (it.data as { opens?: number; clicks?: number; provider?: string; status?: string; report_unit_id?: number | null; to?: string; from?: string }) : null;
  return (
    <li className="flex gap-3 px-4 py-2.5">
      <Icon className={cn("mt-0.5 size-4 shrink-0", dispute ? "text-[var(--color-danger-fg)]" : "text-[var(--color-muted-foreground)]")} aria-hidden />
      <div className="min-w-0 flex-1">
        {note ? (
          <NoteBody note={note} contactId={contactId} />
        ) : (
          <div className="text-sm">
            {it.summary}
            {dispute?.status && (
              <span className="ml-2 align-middle">
                <DisputeBadge p={{ dispute_status: dispute.status, dispute_reason: dispute.reason }} />
              </span>
            )}
            {email && (
              <span className="ml-2 inline-flex flex-wrap items-center gap-1.5 align-middle text-xs text-[var(--color-muted-foreground)]">
                <span>{providerLabel[email.provider ?? ""] ?? email.provider}</span>
                {email.status && !["sent", "imported", "received"].includes(email.status) && <Badge variant="warning">{email.status}</Badge>}
                {email.opens ? <Badge variant="success">opened {email.opens}×</Badge> : null}
                {email.clicks ? <Badge variant="default">clicked {email.clicks}×</Badge> : null}
                {email.report_unit_id ? (
                  <Link to="/email/reports/$reportId" params={{ reportId: String(email.report_unit_id) }} className="inline-flex items-center gap-0.5 text-[var(--color-primary)] hover:underline">
                    <BarChart3 className="size-3" /> report
                  </Link>
                ) : null}
              </span>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-[var(--color-muted-foreground)]">
          <time dateTime={it.occurred_at}>{formatDateTime(it.occurred_at)}</time>
          {it.brand_id ? <BrandMark id={it.brand_id} /> : null}
          {it.actor && <span>by {it.actor}</span>}
        </div>
      </div>
    </li>
  );
}

function NoteBody({ note, contactId }: { note: Note; contactId: number }) {
  const change = useNoteChange(contactId);
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(note.body);
  const locked = note.kind === "merge_snapshot";
  if (editing) {
    return (
      <form
        className="space-y-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          change.mutate({ noteId: note.id, patch: { body } }, { onSuccess: () => (setEditing(false), toast("Note saved")) });
        }}
      >
        <Textarea autoFocus value={body} onChange={(e) => setBody(e.target.value)} className="min-h-20" />
        <div className="flex gap-1.5">
          <Button size="sm" type="submit" disabled={change.isPending || !body.trim()}>
            Save note
          </Button>
          <Button size="sm" variant="ghost" type="button" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      </form>
    );
  }
  return (
    <div className="group text-sm">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {note.title && <div className="font-medium">{note.title}</div>}
          {note.kind !== "note" && <span className="mr-1.5 text-xs text-[var(--color-muted-foreground)]">{note.kind.replace("_", " ")}:</span>}
          <span className={cn("whitespace-pre-wrap", locked && "line-clamp-2 text-[var(--color-muted-foreground)]")}>{note.body}</span>
        </div>
        {!locked && (
          <span className="flex shrink-0 gap-0.5 opacity-60 group-hover:opacity-100">
            <IconButton label={note.pinned ? "Unpin note" : "Pin note to the top"} onClick={() => change.mutate({ noteId: note.id, patch: { pinned: !note.pinned } })}>
              {note.pinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
            </IconButton>
            <IconButton label="Edit note" onClick={() => setEditing(true)}>
              <StickyNote className="size-3.5" />
            </IconButton>
            <IconButton
              label="Delete note"
              onClick={() => {
                if (window.confirm("Delete this note? It is hidden from the timeline but kept in the database.")) change.mutate({ noteId: note.id, remove: true }, { onSuccess: () => toast("Note deleted") });
              }}
            >
              <Trash2 className="size-3.5" />
            </IconButton>
          </span>
        )}
      </div>
    </div>
  );
}

function IconButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} className="rounded p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]">
      {children}
    </button>
  );
}

/** Pinned notes, shown above the tabs so they're seen first. */
export function PinnedNotes({ id }: { id: number }) {
  const notes = useNotes(id);
  const pinned = (notes.data?.notes ?? []).filter((n) => n.pinned);
  if (pinned.length === 0) return null;
  return (
    <Card className="border-[var(--color-warning)]/40">
      <ul className="divide-y">
        {pinned.map((n) => (
          <li key={n.id} className="flex gap-3 px-4 py-2">
            <Pin className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" aria-label="Pinned" />
            <div className="min-w-0 flex-1 text-sm">
              {n.title && <span className="font-medium">{n.title}: </span>}
              <span className="whitespace-pre-wrap">{n.body}</span>
              <div className="text-xs text-[var(--color-muted-foreground)]">
                {formatDateTime(n.created_at)} by {n.created_by}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
